import { Editor } from 'domains/reporter/RichTextEditor/core';
import { useEffect } from 'react';
import type { CreateEnhanceProvider } from '../../types';
import type { GeneralKeyboardControlsPluginPropertyOptions } from './types';
import { useCrossTabEventsListener, NameSpaces } from 'modules/EventsManager';
import { selectNextBracket, selectPreviousBracket } from '../../utils';
import {
  RecorderProvider,
  useRecorder,
  useSendModifyDictationMessage,
} from 'common/Recorder/useRecorder';
import { unreachableCaseLog } from 'types';
import { useMostRecentInput, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { INPUT_TYPES } from 'domains/reporter/inputTypes';
import { ImpressionGeneratorProvider, useImpressionGenerator } from 'hooks/useImpressionGenerator';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from 'domains/extension/constants';
import {
  addExtensionListener,
  removeExtensionListener,
} from 'domains/extension/extensionEventCreators';
import type { SlateProps } from 'slate-react';
import { telemetryContextFor } from 'modules/Apollo/types';
import { RangeMasksProvider } from '../../hooks';
import { AutoImpressionRenderMethod } from '../../analytics/performance';
import { shouldSendModifyFromCrossTabShortcut } from './utils';
import { logger } from 'modules/logger';
import { getPage } from 'utils/page';

type ProviderComponentProps = Readonly<{
  editor: Editor;
  children: React.ReactNode;
  ignoreMergeFieldsInNavigation?: boolean;
}>;

export const ProviderComponent = ({
  children,
  editor,
  ignoreMergeFieldsInNavigation,
  ...rest
}: ProviderComponentProps): React.ReactElement => {
  const { toggleRecording, userActivityElementRef, isRecording } = useRecorder();
  const sendModifyDictationMessage = useSendModifyDictationMessage();
  const { setMostRecentInput } = useMostRecentInput({
    id: RICH_TEXT_EDITOR,
    context: APOLLO_QUERY_CONTEXT,
  });
  const { generateAndStitchImpression } = useImpressionGenerator();

  // Handles in-app cross-tab reporter shortcuts broadcast via NameSpaces.REPORTER_SHORTCUT.
  // This path runs for shortcuts triggered from any Sirona window (Reporter/Viewer/Worklist/etc.):
  // `source: 'local'` is the current window and `source: 'remote'` is another window.
  useCrossTabEventsListener(NameSpaces.REPORTER_SHORTCUT, ({ source, payload }) => {
    setMostRecentInput(INPUT_TYPES.KEYBOARD);

    const { action, type } = payload;
    const page = getPage();
    const baseShortcutLogContext = {
      channel: NameSpaces.REPORTER_SHORTCUT,
      source,
      action,
      pageType: page.type,
    };
    const toggleShortcutLogContext = {
      ...baseShortcutLogContext,
      intendedTransition: isRecording ? 'stop' : 'start',
    };

    switch (action) {
      case 'nextBracket':
        logger.info('[enhanceProviderGeneralKeyboardControls] keyboard shortcut navigation', {
          ...baseShortcutLogContext,
        });
        if (
          shouldSendModifyFromCrossTabShortcut({
            source,
            activeElement: document.activeElement,
            userActivityElement: userActivityElementRef.current,
          })
        ) {
          sendModifyDictationMessage();
        }
        selectNextBracket(editor, { ignoreMergeFieldsInNavigation });
        break;
      case 'previousBracket':
        logger.info('[enhanceProviderGeneralKeyboardControls] keyboard shortcut navigation', {
          ...baseShortcutLogContext,
        });
        if (
          shouldSendModifyFromCrossTabShortcut({
            source,
            activeElement: document.activeElement,
            userActivityElement: userActivityElementRef.current,
          })
        ) {
          sendModifyDictationMessage();
        }
        selectPreviousBracket(editor, {
          ignoreMergeFieldsInNavigation,
        });
        break;
      case 'toggleRecording':
        logger.info('[enhanceProviderGeneralKeyboardControls] keyboard shortcut toggle recording', {
          ...toggleShortcutLogContext,
        });
        toggleRecording();
        break;
      case 'toggleCommandMode':
        // Command Mode toggle is handled by Recorder; intentional no-op here.
        break;
      case 'draftReport':
      case 'submitReport':
      case 'discardReport':
        // This is handled in Fields.Context so that its provider does not need to be lifted further.
        break;
      case 'generateImpression':
        generateAndStitchImpression(editor, false, {
          triggerMethod:
            type === 'gamepad'
              ? AutoImpressionRenderMethod.DictaphoneButtonAction
              : AutoImpressionRenderMethod.KeyboardShortcut,
        });
        break;

      default:
        unreachableCaseLog(action);
        return;
    }
  });

  useEffect(() => {
    // Handles extension-originated shortcut events (srna.shortcut.triggered), which are separate
    // from the app's cross-tab event bus above. Use this when shortcuts come from the extension
    // bridge rather than NameSpaces.REPORTER_SHORTCUT broadcasts.
    const handler = ((event: CustomEvent<string>) => {
      const { detail } = event;
      setMostRecentInput(INPUT_TYPES.KEYBOARD);
      const page = getPage();
      const baseExtensionShortcutLogContext = {
        channel: EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED,
        source: 'extension',
        action: detail,
        pageType: page.type,
      };
      const toggleExtensionShortcutLogContext = {
        ...baseExtensionShortcutLogContext,
        intendedTransition: isRecording ? 'stop' : 'start',
      };
      switch (detail) {
        case 'reporter-next-field':
          logger.info(
            '[enhanceProviderGeneralKeyboardControls] extension keyboard shortcut navigation',
            { ...baseExtensionShortcutLogContext }
          );
          sendModifyDictationMessage();
          selectNextBracket(editor, { ignoreMergeFieldsInNavigation });
          break;
        case 'reporter-previous-field':
          logger.info(
            '[enhanceProviderGeneralKeyboardControls] extension keyboard shortcut navigation',
            { ...baseExtensionShortcutLogContext }
          );
          sendModifyDictationMessage();
          selectPreviousBracket(editor, { ignoreMergeFieldsInNavigation });
          break;
        case 'reporter-toggle-recording':
          logger.info(
            '[enhanceProviderGeneralKeyboardControls] extension keyboard shortcut toggle recording',
            { ...toggleExtensionShortcutLogContext }
          );
          toggleRecording();
          break;
        case 'reporter-submit-report':
        case 'reporter-discard-report':
          // This is handled in Fields.Context so that its provider does not need to be lifted further.
          break;
        case 'reporter-generate-impression':
          generateAndStitchImpression(editor, false, {
            triggerMethod: AutoImpressionRenderMethod.KeyboardShortcut,
          });
          break;
        default:
          unreachableCaseLog(detail);
          return;
      }
    }) as EventListener;

    addExtensionListener(EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED, handler);

    return () => {
      removeExtensionListener(EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED, handler);
    };
  }, [
    editor,
    generateAndStitchImpression,

    ignoreMergeFieldsInNavigation,
    setMostRecentInput,
    sendModifyDictationMessage,
    isRecording,
    toggleRecording,
  ]);

  return <>{children}</>;
};

export const enhanceProviderGeneralKeyboardControls: CreateEnhanceProvider<
  GeneralKeyboardControlsPluginPropertyOptions
> =
  ({ pluginID }) =>
  (Component: React.ComponentType<SlateProps>) =>
    function EnhanceProviderGeneralKeyboardControls(props: SlateProps) {
      return (
        <Component {...props}>
          <RecorderProvider>
            <ImpressionGeneratorProvider>
              <RangeMasksProvider>
                <ProviderComponent editor={props.editor}>{props.children}</ProviderComponent>
              </RangeMasksProvider>
            </ImpressionGeneratorProvider>
          </RecorderProvider>
        </Component>
      );
    };

ProviderComponent.displayName = 'GeneralKeyboardControlProviderComponent';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ProviderComponent);
